/**
 * @file TabPromises.jsx
 * @description TabPromises component for displaying and managing tab promises.
 * @component TabPromises
 * @exports TabPromises - Main component for displaying and managing tab promises.
 */

import React, { useState, useEffect, useMemo } from "react";
import Grid from "@mui/material/Grid2";
import { TextField, Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, LinearProgress, TablePagination } from "@mui/material";
import { fetchPromisesByCustomer, fetchPromisesByCustomerChart } from "../../../services/customerModule/payments/paymentService";
import { SIZES, SORT_ORDERS } from "../../../common/constants/commonConstants";
import { PAYMENT_STRINGS, PAYMENT_ERRORS, PAYMENT_FIELDS } from "../../../common/constants/customerModule/paymentConstants";
import CustomerDashPromisesChart from "../../dashboard/customersDash/charts/CustomerDashPromisesChart";
import TableSortLabel from '@mui/material/TableSortLabel';
import { getComparator, handleSort } from "../../../hooks/useTable";

const CustomerPromises = ({ customerguid = null, isDash = false }) => {
	const [promises, setPromises] = useState([]);
	const [search, setSearch] = useState("");

	const [isLoading, setIsLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);

	const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
	const [orderBy, setOrderBy] = useState(PAYMENT_FIELDS.CUSTOMER_NAME);

	useEffect(() => {
		let mounted = true;
		const load = async () => {
			if (!customerguid) {
				if (mounted) setIsLoading(false);
				return;
			}
			try {
				setIsLoading(true);
				const data = await fetchPromisesByCustomer(customerguid,  'Promise');
				if (mounted) setPromises(data || []);
			} catch (error) {
				console.error(PAYMENT_ERRORS.FETCH_PROMISES_ERROR, error);
				if (mounted) setPromises([]);
			} finally {
				if (mounted) setIsLoading(false);
			}
		};
		load();
		return () => { mounted = false; };
	}, [customerguid]);

	const [chartData, setChartData] = useState([]);

	useEffect(() => {
		const fetchChart = async () => {
			try {
				const data = await fetchPromisesByCustomerChart(customerguid);
				setChartData(data || []);
			} catch (error) {
				console.error("Error fetching promises chart:", error);
			}
		};

		if (isDash && customerguid) {
			fetchChart();
		}
	}, [customerguid, isDash]);

	const promisesToDisplay = useMemo(() => {
		const q = (search || "").toLowerCase().trim();
		const filtered = promises.filter((p) => {
			if (!q) return true;
			const dateStr = p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-US') : "";
			return (
				dateStr.toLowerCase().includes(q) ||
				(p.customerId || "").toString().toLowerCase().includes(q) ||
				(p.customerName || "").toLowerCase().includes(q)
			);
		});
		return [...filtered].sort(getComparator(order, orderBy));
	}, [promises, search, order, orderBy]);

	const pagedPromises = useMemo(() => {
		const start = page * rowsPerPage;
		return promisesToDisplay.slice(start, start + rowsPerPage);
	}, [promisesToDisplay, page, rowsPerPage]);

	const handleRequestSort = (property) => {
		handleSort(order, orderBy, property, setOrder, setOrderBy);
	};

	useEffect(() => { setPage(0); }, [search]);

	return (
		<Container width="xl" disableGutters sx={{ p: 2 }}>
			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid size={SIZES.GROW}>
					<TextField
						fullWidth
						size={SIZES.SMALL}
						value={search}
						variant="outlined"
						placeholder={PAYMENT_STRINGS.SEARCH_PLACEHOLDER}
						onChange={e => setSearch(e.target.value)}
					/>
				</Grid>
			</Grid>

			{isDash && (
				<CustomerDashPromisesChart
					rows={chartData}
					title="Promise Volume"
					label="Promises"
					isCurrency={true}
				/>
			)}

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell align="center">{PAYMENT_STRINGS.DATE}</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === PAYMENT_FIELDS.PAYMENT_TYPE}
									direction={orderBy === PAYMENT_FIELDS.PAYMENT_TYPE ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort(PAYMENT_FIELDS.PAYMENT_TYPE)}
								>
									{PAYMENT_STRINGS.TYPE}
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">{PAYMENT_STRINGS.CUSTOMER_ID}</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === PAYMENT_FIELDS.CUSTOMER_NAME}
									direction={orderBy === PAYMENT_FIELDS.CUSTOMER_NAME ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort(PAYMENT_FIELDS.CUSTOMER_NAME)}
								>
									{PAYMENT_STRINGS.CUSTOMER}
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === PAYMENT_FIELDS.CURRENCY}
									direction={orderBy === PAYMENT_FIELDS.CURRENCY ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort(PAYMENT_FIELDS.CURRENCY)}
								>
									{PAYMENT_STRINGS.CURRENCY}
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === PAYMENT_FIELDS.PAYMENT_AMOUNT}
									direction={orderBy === PAYMENT_FIELDS.PAYMENT_AMOUNT ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort(PAYMENT_FIELDS.PAYMENT_AMOUNT)}
								>
									{PAYMENT_STRINGS.AMOUNT}
								</TableSortLabel>
							</TableCell>
							<TableCell align="center">
								<TableSortLabel
									active={orderBy === PAYMENT_FIELDS.PAYMENT_METHOD}
									direction={orderBy === PAYMENT_FIELDS.PAYMENT_METHOD ? order : SORT_ORDERS.ASCENDING}
									onClick={() => handleRequestSort(PAYMENT_FIELDS.PAYMENT_METHOD)}
								>
									{PAYMENT_STRINGS.PAYMENT_METHOD}
								</TableSortLabel>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{isLoading ? (
							<TableRow>
								<TableCell colSpan={7} align="center">
									<LinearProgress />
								</TableCell>
							</TableRow>
						) : promises.length > 0 ? (
							pagedPromises.map((p, index) => (
								<TableRow key={index}>
									<TableCell align="center">{p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('en-US') : PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.paymentType || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.customerId || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.customerName || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.currency || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.paymentAmount != null ? p.paymentAmount : PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
									<TableCell align="center">{p.paymentMethod || PAYMENT_STRINGS.NOT_AVAILABLE}</TableCell>
								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={7} align="center">
									No promises found
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				page={page}
				rowsPerPage={rowsPerPage}
				count={promisesToDisplay.length}
				rowsPerPageOptions={[10, 25, 50]}
				onPageChange={(event, newPage) => setPage(newPage)}
				onRowsPerPageChange={(event) => {
					setPage(0);
					setRowsPerPage(parseInt(event.target.value, 10));
				}}
			/>
		</Container>
	);
};

export default CustomerPromises;